// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Tracks variant trait selections for parent products displayed on the Product List Page (PLP).
 *
 * State shape:
 *   variantSelectionsByProductId[parentProductId][variantTraitId] = traitValueId
 *
 * Dispatch "XKeller/UpdateProductListVariantSelection" when the user changes a dropdown on a PLP card.
 */
import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { ProductInventoryDto, ProductPriceDto, RealTimeInventoryModel, RealTimePricingModel } from "@insite/client-framework/Types/ApiModels";
import { Draft } from "immer";

export interface XProductListVariantsStateModel {
    variantSelectionsByProductId: SafeDictionary<SafeDictionary<string>>;
    /** Pricing results keyed by variant product ID, populated via XKeller/CompleteLoadVariantPricing */
    variantPricingByProductId: SafeDictionary<ProductPriceDto>;
    /** Inventory results keyed by variant product ID, populated via XKeller/CompleteLoadVariantInventory */
    variantInventoryByProductId: SafeDictionary<ProductInventoryDto>;
}

export const initialXProductListVariants: XProductListVariantsStateModel = {
    variantSelectionsByProductId: {},
    variantPricingByProductId: {},
    variantInventoryByProductId: {},
};

const reducer = {
    "XKeller/UpdateProductListVariantSelection": (
        draft: Draft<XProductListVariantsStateModel>,
        action: { parentProductId: string; variantTraitId: string; traitValueId: string },
    ) => {
        if (!draft.variantSelectionsByProductId[action.parentProductId]) {
            draft.variantSelectionsByProductId[action.parentProductId] = {};
        }
        draft.variantSelectionsByProductId[action.parentProductId]![action.variantTraitId] = action.traitValueId;
    },
    /** Stores real-time pricing results for variant children returned from the pricing API */
    "XKeller/CompleteLoadVariantPricing": (
        draft: Draft<XProductListVariantsStateModel>,
        action: { realTimePricing: RealTimePricingModel },
    ) => {
        action.realTimePricing.realTimePricingResults?.forEach(pricing => {
            draft.variantPricingByProductId[pricing.productId] = pricing;
        });
    },
    /** Stores real-time inventory results for variant children returned from the inventory API */
    "XKeller/CompleteLoadVariantInventory": (
        draft: Draft<XProductListVariantsStateModel>,
        action: { realTimeInventory: RealTimeInventoryModel },
    ) => {
        action.realTimeInventory.realTimeInventoryResults?.forEach(inventory => {
            draft.variantInventoryByProductId[inventory.productId] = inventory;
        });
    },
};

const xProductListVariants = createTypedReducerWithImmer(initialXProductListVariants, reducer);

(reducers as any).xProductListVariants = xProductListVariants;

export type xProductListVariantsState = {
    xProductListVariants: XProductListVariantsStateModel;
} & ApplicationState;

type xProductListVariantsReducers = {
    [key: string]: typeof xProductListVariants;
};

export type xProductListVariantsActions =
    | Parameters<xProductListVariantsReducers[keyof xProductListVariantsReducers]>[1]
    | AnyAction;
